// The poster gallery's data (2026-10-26; fullstack claude/POSTERS.md): every published poster from
// the fullstack's /sitemaps/posters.json (scripts/update/posters: title, authors, address,
// category, session, the small thumbnail's width and height). The images stay on the old poster
// server (www.vizbi.org/Posters/Images/<year>/Thumbnails_<Small|Medium|Large>/<poster>.png).
import {computed, ref} from "vue";

const IMAGES = "https://www.vizbi.org/Posters/Images";
const posters = ref([]);
let loading = null;

// the gallery's categories (Seán, 2026-10-26): VIZBI's six session categories in the program's
// order, Art & Biology first and Other last, in groups with a line between them in the menu
// (molecules; cells to ecosystems). Each poster's own (posters.json, from the old database: nine
// names) is mapped to one of them; a name not listed is Other
const CATEGORY_GROUPS = [
  ["Art & Biology"], ["DNA", "RNA", "Proteins"], ["Cells", "Tissues", "Ecosystems"], ["Other"]
];
export const CATEGORIES = CATEGORY_GROUPS.flat();
const CATEGORY_BY_NAME = {
  Genomes: "DNA", Transcripts: "RNA", "Cellular Systems": "Cells", Organisms: "Tissues",
  Populations: "Ecosystems"
};

export function getCategory(name) {
  const category = CATEGORY_BY_NAME[name] || name;
  return CATEGORIES.includes(category) ? category : "Other";
}

async function fetchPosters() {
  const response = await fetch("/sitemaps/posters.json");
  if (!response.ok) throw new Error(`posters.json: ${response.status}`);
  const list = await response.json();
  posters.value = list.map((poster) => ({...poster, category: getCategory(poster.category)}));
}

const TILE = 256; // px: the pyramids' tiles (each level of 256 px or less is one file)

// the pyramid's smallest level at least this tall, if it's one tile: each level halves the one
// above, down to 1 px (Deep Zoom)
function getPyramidLevel({width, height}, minHeight) {
  const top = Math.ceil(Math.log2(Math.max(width, height)));
  for (let level = 0; level <= top; level++) {
    const scale = 2 ** (top - level);
    if (Math.ceil(width / scale) > TILE || Math.ceil(height / scale) > TILE) return null;
    if (Math.ceil(height / scale) >= minHeight) return level;
  }
  return null;
}

// the pyramid's largest level that's one tile (the whole poster at 256 px or less), as
// OpenSeadragon's lowest level drawn
export function getOverviewUrl({year, posterNum, pyramid}) {
  const longest = Math.max(pyramid.width, pyramid.height);
  const level = Math.ceil(Math.log2(longest)) - Math.max(0, Math.ceil(Math.log2(longest / TILE)));
  return `${IMAGES}/${year}/${posterNum}_files/${level}/0_0.png`;
}

// a row this high: the one pyramid tile just tall enough on this screen (2× on a Retina; Option
// B, 2026-10-26: a tenth of the bytes of the thumbnails at the gallery's small rows), else a
// thumbnail: the small one (100 px) up to 100, then medium (333), large (600), and for the
// few posters without a pyramid (posters.json: pyramid false)
export function getThumbnailUrl({year, posterNum, pyramid}, rowHeight) {
  const minHeight = rowHeight * (window.devicePixelRatio || 1);
  const level = pyramid ? getPyramidLevel(pyramid, minHeight) : null;
  if (level !== null) return `${IMAGES}/${year}/${posterNum}_files/${level}/0_0.png`;
  const size = rowHeight > 250 ? "Large" : rowHeight > 100 ? "Medium" : "Small";
  return `${IMAGES}/${year}/Thumbnails_${size}/${posterNum}.png`;
}

// one poster's own page: its abstract, link and hasGif, from its year's file (cached per year)
const detailsByYear = {};
async function fetchYearDetails(year) {
  const response = await fetch(`/sitemaps/posters/${year}.json`);
  if (!response.ok) throw new Error(`posters/${year}.json: ${response.status}`);
  return response.json();
}

export async function fetchPosterDetails(year, posterNum) {
  if (!detailsByYear[year]) {
    detailsByYear[year] = fetchYearDetails(year).catch((error) => {
      delete detailsByYear[year]; // asked again next time
      throw error;
    });
  }
  const details = await detailsByYear[year];
  return details[posterNum] || null;
}

// the old server's full-size files for a poster: its zoomable tiles (.xml), image and GIF
export const getPosterFileUrl = ({year, posterNum}, extension) => {
  return `${IMAGES}/${year}/${posterNum}.${extension}`;
};

// a single poster's address (/Posters/2025/Y14): its year and number, else null (a gallery)
export function getPosterAddress(route) {
  if (route.name !== "Posters") return null;
  const parts = [route.params.specification].flat().map(String);
  const year = parts.find((part) => /^\d{4}$/.test(part));
  const posterNum = parts.find((part) => /^v?[A-Z]\d\d$/.test(part));
  return year && posterNum ? {year, posterNum} : null;
}

const getUnique = (values) => [...new Set(values.filter(Boolean))];

export function usePosters() {
  if (!loading) loading = fetchPosters().catch((error) => console.error(error));
  const years = computed(() => getUnique(posters.value.map((poster) => poster.year)));
  const categories = computed(() => {
    const shown = new Set(posters.value.map((poster) => poster.category));
    return CATEGORIES.filter((category) => shown.has(category));
  });
  // the last category shown of each group but the last: the menu's lines under them
  const categorySeparators = computed(() => {
    const getLast = (group) => group.filter((name) => categories.value.includes(name)).at(-1);
    return CATEGORY_GROUPS.slice(0, -1).map(getLast).filter(Boolean);
  });
  return {posters, years, categories, categorySeparators, loading};
}
